import { cn, NcButton, NcIconChevronDown, NcMenu } from "@noted/noted-components";
import { ReactNode } from "react";
import { Route, Routes } from "react-router-dom";

import { FeaturePermission } from "~/api/graphql-types";
import { useAccount } from "~/components/account-context/account.context";
import { useFeatures } from "~/components/features.context";
import {
  Acc,
  Administration,
  Apps,
  Auditing,
  Client,
  PRIMHD,
  Reporting,
  Scheduling,
} from "~/components/icons";
import { useI18n } from "~/hooks/use-i18n";

type ConditionalMenuItemProps = {
  onClick?: () => void;
  condition?: boolean;
  children: ReactNode;
  to: string;
};

const ConditionalMenuItem = ({ condition = true, children, to }: ConditionalMenuItemProps) => {
  if (!condition) return null;

  return (
    <NcMenu.Item href={to} className="gap-3 py-2.5">
      {children}
    </NcMenu.Item>
  );
};

const AppsMenu = () => {
  const { t } = useI18n();
  const { featureEnabled } = useFeatures();
  const { account, canSchedule, organisation, privilegeEnabled } = useAccount();
  const accEnabled =
    organisation.healthLinkKeystoreStored && privilegeEnabled("PRIV_CAN_INVOICE_ACC_CLAIMS");
  const aReportingFeatureIsEnabled =
    featureEnabled(FeaturePermission.Reports) || featureEnabled(FeaturePermission.Extracts);
  const defaultReportingPath = featureEnabled(FeaturePermission.Reports)
    ? "/n/reports"
    : "/n/extract";

  return (
    <>
      <ConditionalMenuItem to={`/dashboard`}>
        <Client width="14" />
        <span>{t("clients_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem
        condition={featureEnabled(FeaturePermission.AccBilling) && Boolean(accEnabled)}
        to={`/n/acc/invoicing`}
      >
        <Acc width="17" />
        <span>{t("acc_billing_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem
        condition={
          featureEnabled(FeaturePermission.Primhd) && privilegeEnabled("PRIV_CAN_MANAGE_PRIMHD")
        }
        to="/primhd"
      >
        <PRIMHD width="13" />
        <span>{t("primhd_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem to={defaultReportingPath} condition={aReportingFeatureIsEnabled}>
        <Reporting width="14" />
        <span>{t("reporting_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem
        condition={featureEnabled(FeaturePermission.BookingSystem) && canSchedule}
        to="/scheduling"
      >
        <Scheduling width="14" />
        <span>{t("scheduling_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem condition={featureEnabled(FeaturePermission.AuditTrail)} to="/auditing">
        <Auditing width="18" />
        <span>{t("auditing_app")}</span>
      </ConditionalMenuItem>

      <ConditionalMenuItem condition={account.isAdminUser} to="/admin">
        <Administration width="14" />
        <span>{t("administration_app")}</span>
      </ConditionalMenuItem>
    </>
  );
};

export const ApplicationsSwitcher = ({
  sideBarCollapsed,
}: {
  role?: string;
  sideBarCollapsed?: boolean;
}) => {
  const { t } = useI18n();
  const { account } = useAccount();

  return (
    <NcMenu.Trigger>
      <NcButton className="bg-brand-active text-light data-pressed:bg-main-dark data-focus-visible:outline-light w-full justify-start gap-3 border-none">
        <Apps height="22" width="16" />
        <span className={cn("truncate", sideBarCollapsed && "lg:sr-only")}>
          <Routes>
            {account.isAdminUser && <Route path="/admin/*" element={t("administration_app")} />}
            <Route path="/scheduling/*" element={t("scheduling_app")} />
            <Route path="/primhd/*" element={t("primhd_app")} />
            <Route path="/auditing/*" element={t("auditing_app")} />
            <Route path="/n/acc/*" element={t("acc_billing_app")} />
            <Route path="/n/extract/*" element={t("reporting_app")} />
            <Route path="/n/reports/*" element={t("reporting_app")} />
            <Route path="/n/*" element={t("clients_app")} />
            <Route path="/dashboard" element={t("clients_app")} />
            <Route path="/user-settings/*" element={t("clients_app")} />
          </Routes>
        </span>
        <NcIconChevronDown
          className={cn(
            "allow-discrete ml-auto shrink-0 transition-opacity delay-100 duration-150 starting:opacity-0",
            sideBarCollapsed && "opacity-0 lg:hidden"
          )}
        />
      </NcButton>
      <NcMenu aria-label={t("app_sections")} className="border-none">
        <AppsMenu />
      </NcMenu>
    </NcMenu.Trigger>
  );
};
